import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { validateToken } from '../slices/authSlice';
import AdminPasswordPrompt from './AdminPasswordPrompt';

const VALIDATION_TIMEOUT_MS = 8000;

const PrivateRoute = ({ element }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { token, isTokenValidating } = useSelector((state) => state.auth);
    const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
    const [hasValidatedToken, setHasValidatedToken] = useState(false);

    // Vérification de la validité du token (avec timeout pour ne pas bloquer indéfiniment)
    useEffect(() => {
        if (!token) {
            setHasValidatedToken(true);
            return;
        }
        if (hasValidatedToken) return;

        let cancelled = false;
        const timeoutId = setTimeout(() => {
            if (!cancelled) setHasValidatedToken(true);
        }, VALIDATION_TIMEOUT_MS);

        const checkToken = async () => {
            try {
                await dispatch(validateToken());
                if (!cancelled) setHasValidatedToken(true);
            } catch {
                if (!cancelled) setHasValidatedToken(true);
            } finally {
                if (!cancelled) clearTimeout(timeoutId);
            }
        };
        checkToken();
        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [dispatch, token, hasValidatedToken]);

    // Affichage du loader uniquement tant que notre validation n'est pas terminée (timeout ou réponse API)
    // On ne dépend pas de isTokenValidating : si l'API bloque, elle reste true et bloquerait indéfiniment
    if (token && !hasValidatedToken) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Vérification de votre session...</p>
                </div>
            </div>
        );
    }

    // Vérification si l'utilisateur est connecté
    if (!token) {
        const to = location.pathname === '/subscription'
            ? '/login?redirect=/subscription'
            : '/login';
        return <Navigate to={to} replace />;
    }

    // Gestion de l'accès à l'admin avec un mot de passe
    if (element.type && element.type.name === 'AdminDashboard') {
        if (!isAdminAuthenticated) {
            return <AdminPasswordPrompt onAccessGranted={setIsAdminAuthenticated} />;
        }
    }

    return element;
};

export default PrivateRoute;
